import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Avatar,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import axios from "axios";
import { API_URL } from "../config";
import DataTable from "react-data-table-component";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CampaignIcon from "@mui/icons-material/Campaign";
import SchoolIcon from "@mui/icons-material/School";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";


// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A" },
    error: { main: "#DC2626" },
    success: { main: "#059669" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1060, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));


const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const TabToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  background: alpha(theme.palette.primary.main, 0.06),
  borderRadius: 12,
  padding: 4,
  gap: 3,
  width: "fit-content",
}));

const TabBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  borderRadius: 9,
  padding: "8px 20px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "none",
  transition: "all 0.18s ease",
  ...(active
    ? {
        background: "#fff",
        color: theme.palette.primary.main,
        boxShadow: "0 1px 6px rgba(0,0,0,0.10)",
        "&:hover": { background: "#fff" },
      }
    : {
        background: "transparent",
        color: theme.palette.text.disabled,
        "&:hover": {
          background: alpha("#fff", 0.5),
          color: theme.palette.text.secondary,
        },
      }),
}));

const StatBox = styled(Paper, {
  shouldForwardProp: (p) => p !== "accentcolor" && p !== "delay",
})(({ accentcolor, delay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(accentcolor || "#2563EB", 0.12)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  background: "#fff",
  flex: 1,
  minWidth: 140,
}));

const StatIconBox = styled(Box)(({ accentcolor }) => ({
  width: 42,
  height: 42,
  borderRadius: 11,
  flexShrink: 0,
  background: alpha(accentcolor || "#2563EB", 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    assign: { bg: alpha("#2563EB", 0.08), color: "#2563EB", hbg: "#2563EB" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.assign;
  return {
    minWidth: "unset",
    padding: "5px 12px",
    borderRadius: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});



const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
      animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
    }}
  />
);

// ─── Custom DataTable styles ──────────────────────────────────────────────────
const tableCustomStyles = {
  table: {
    style: { fontFamily: "'Sora', sans-serif", background: "transparent" },
  },
  headRow: {
    style: {
      background: alpha("#F0F2F8", 0.8),
      borderRadius: "10px 10px 0 0",
      borderBottom: `1px solid ${alpha("#000", 0.07)}`,
      minHeight: 44,
    },
  },
  headCells: {
    style: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.7px",
      color: "#9CA3AF",
      fontFamily: "'Sora', sans-serif",
      paddingLeft: 16,
    },
  },
  rows: {
    style: {
      fontSize: 13,
      fontFamily: "'Sora', sans-serif",
      color: "#4B5563",
      minHeight: 56,
      borderBottom: `1px solid ${alpha("#000", 0.05)}`,
      transition: "background 0.15s ease",
      "&:last-child": { borderBottom: "none" },
    },
    highlightOnHoverStyle: {
      background: alpha("#2563EB", 0.03),
      borderRadius: 8,
      outline: "none",
      cursor: "default",
    },
  },
  cells: { style: { paddingLeft: 16 } },
  pagination: {
    style: {
      fontFamily: "'Sora', sans-serif",
      fontSize: 12,
      fontWeight: 600,
      color: "#4B5563",
      borderTop: `1px solid ${alpha("#000", 0.06)}`,
      padding: "12px 16px",
    },
  },
  noData: {
    style: {
      fontFamily: "'Sora', sans-serif",
      color: "#9CA3AF",
      padding: "40px 0",
    },
  },
};

// ─── Main Component ───────────────────────────────────────────────────────────
function PromoCodeDashBoard() {
  const [tab, setTab] = useState(0);
  const [promoData, setPromoData] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [loadingPromo, setLoadingPromo] = useState(true);
  const [loadingMarketers, setLoadingMarketers] = useState(true);
  const { countryId } = useParams();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const ts = localStorage.getItem("authToken");
    return ts ? JSON.parse(ts) : null;
  }, []);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token?.access}` };

    setLoadingPromo(true);
    axios
      .get(`${API_URL}/admin/promo-code-user-count/${countryId}/`, { headers })
      .then((res) => setPromoData(res.data))
      .catch(console.error)
      .finally(() => setLoadingPromo(false));

    setLoadingMarketers(true);
    axios
      .get(`${API_URL}/admin/marketers/${countryId}/`, { headers })
      .then((res) => setMarketers(res.data))
      .catch(console.error)
      .finally(() => setLoadingMarketers(false));
  }, [countryId, token]);

  const handleAssignSchool = async (marketerId) => {
    await axios.put(`${API_URL}/admin/marketers/${countryId}/`, {
      marketer_id: marketerId,
      school_id: 123,
    });
  };

  const handleDeleteMarketer = async (marketerId) => {
    await axios.delete(
      `${API_URL}/admin/marketers/${countryId}/${marketerId}/`
    );
    setMarketers((prev) => prev.filter((m) => m.id !== marketerId));
  };

  // ── Stats ──
  const totalPromoProfit = promoData.reduce((s, i) => s + (i.profit || 0), 0);
  const totalPromoUsers = promoData.reduce(
    (s, i) => s + (i.user_count || 0),
    0
  );
  

  // ── Promo columns ──
  const promoColumns = [
    {
      name: "User",
      selector: (row) => row.user,
      sortable: true,
      cell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              background: alpha("#2563EB", 0.12),
              color: "#2563EB",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.user?.[0]?.toUpperCase() || "?"}
          </Avatar>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F1117",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.user}
          </Typography>
        </Box>
      ),
      grow: 2,
    },
    {
      name: "Promo ID",
      selector: (row) => row.promo_id,
      sortable: true,
      cell: (row) => (
        <Chip
          label={row.promo_id}
          size="small"
          sx={{
            fontFamily: "'Sora', sans-serif",
            fontWeight: 700,
            fontSize: 11,
            background: alpha("#2563EB", 0.07),
            color: "#2563EB",
            border: `1px solid ${alpha("#2563EB", 0.18)}`,
          }}
        />
      ),
    },
    {
      name: "Users",
      selector: (row) => row.user_count,
      sortable: true,
      cell: (row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            background: alpha("#059669", 0.07),
            border: `1px solid ${alpha("#059669", 0.18)}`,
            borderRadius: 2,
            px: 1.2,
            py: 0.5,
          }}
        >
          <PeopleAltIcon sx={{ fontSize: 12, color: "#059669" }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#059669",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.user_count}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Profit (₦)",
      selector: (row) => row.profit,
      sortable: true,
      cell: (row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            background: alpha("#C4922A", 0.08),
            border: `1px solid ${alpha("#C4922A", 0.2)}`,
            borderRadius: 2,
            px: 1.2,
            py: 0.5,
          }}
        >
          <MonetizationOnIcon sx={{ fontSize: 12, color: "#C4922A" }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#C4922A",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.profit?.toLocaleString()}
          </Typography>
        </Box>
      ),
    },
  ];

  // ── Marketer columns ──
  const marketerColumns = [
    {
      name: "Marketer",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #7C3AED, #5B21B6)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.name?.[0]?.toUpperCase() || "?"}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#0F1117",
                fontFamily: "'Sora', sans-serif",
                lineHeight: 1.2,
              }}
            >
              {row.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: "text.disabled",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              ID: {row.id}
            </Typography>
          </Box>
        </Box>
      ),
      grow: 2,
    },
    {
      name: "Profit (₦)",
      selector: (row) => row.profit,
      sortable: true,
      cell: (row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            background: alpha("#C4922A", 0.08),
            border: `1px solid ${alpha("#C4922A", 0.2)}`,
            borderRadius: 2,
            px: 1.2,
            py: 0.5,
          }}
        >
          <MonetizationOnIcon sx={{ fontSize: 12, color: "#C4922A" }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#C4922A",
              fontFamily: "'Sora', sans-serif",
            }}
          >
            {row.profit?.toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Schools",
      selector: (row) => row.schools,
      cell: (row) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
          {row.schools?.length > 0 ? (
            row.schools.map((s, i) => (
              <Chip
                key={i}
                label={s.username}
                size="small"
                icon={<SchoolIcon sx={{ fontSize: "10px !important" }} />}
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 600,
                  fontSize: 10,
                  background: alpha("#059669", 0.07),
                  color: "#059669",
                  border: `1px solid ${alpha("#059669", 0.18)}`,
                  "& .MuiChip-icon": { color: "#059669" },
                }}
              />
            ))
          ) : (
            <Typography
              sx={{
                fontSize: 11,
                color: "text.disabled",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              No schools
            </Typography>
          )}
        </Box>
      ),
      grow: 2,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Stack direction="row" spacing={0.8}>
          <Tooltip title="Assign school" arrow>
            <ActionBtn
              btncolor="assign"
              onClick={() => handleAssignSchool(row.id)}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
              Assign
            </ActionBtn>
          </Tooltip>
          <Tooltip title="Delete marketer" arrow>
            <ActionBtn
              btncolor="delete"
              onClick={() => handleDeleteMarketer(row.id)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
              Delete
            </ActionBtn>
          </Tooltip>
        </Stack>
      ),
      minWidth: "180px",
    },
  ];

  const isLoading = tab === 0 ? loadingPromo : loadingMarketers;

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 28 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Promo Dashboard
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Country ID:{" "}
                <strong style={{ color: "#4B5563" }}>{countryId}</strong>
              </Typography>
            </Box>
          </Box>

          {/* ── Stat boxes ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <StatBox accentcolor="#2563EB" delay={50}>
              <StatIconBox accentcolor="#2563EB">
                <LocalOfferIcon sx={{ fontSize: 20, color: "#2563EB" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {promoData.length}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Promo Codes
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#059669" delay={100}>
              <StatIconBox accentcolor="#059669">
                <PeopleAltIcon sx={{ fontSize: 20, color: "#059669" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {totalPromoUsers}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Promo Users
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#C4922A" delay={150}>
              <StatIconBox accentcolor="#C4922A">
                <MonetizationOnIcon sx={{ fontSize: 20, color: "#C4922A" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  ₦{totalPromoProfit.toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Promo Profit
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#7C3AED" delay={200}>
              <StatIconBox accentcolor="#7C3AED">
                <CampaignIcon sx={{ fontSize: 20, color: "#7C3AED" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {marketers.length}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Marketers
                </Typography>
              </Box>
            </StatBox>
          </Box>

          {/* ── Table card ── */}
          <SectionCard delay={240}>
            {/* Tab toggle + count chip */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <TabToggle>
                <TabBtn
                  active={tab === 0 ? 1 : 0}
                  startIcon={
                    <LocalOfferIcon sx={{ fontSize: "14px !important" }} />
                  }
                  onClick={() => setTab(0)}
                >
                  Promo Codes
                </TabBtn>
                <TabBtn
                  active={tab === 1 ? 1 : 0}
                  startIcon={
                    <CampaignIcon sx={{ fontSize: "14px !important" }} />
                  }
                  onClick={() => setTab(1)}
                >
                  Marketers
                </TabBtn>
              </TabToggle>

              <Chip
                label={
                  tab === 0
                    ? `${promoData.length} code${
                        promoData.length !== 1 ? "s" : ""
                      }`
                    : `${marketers.length} marketer${
                        marketers.length !== 1 ? "s" : ""
                      }`
                }
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha(tab === 0 ? "#2563EB" : "#7C3AED", 0.08),
                  color: tab === 0 ? "#2563EB" : "#7C3AED",
                  border: `1px solid ${alpha(
                    tab === 0 ? "#2563EB" : "#7C3AED",
                    0.18
                  )}`,
                }}
              />
            </Box>

            {/* Loading skeleton */}
            {isLoading ? (
              <Box>
                {[...Array(5)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 2,
                      borderBottom: `1px solid ${alpha("#000", 0.05)}`,
                      animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
                      animationDelay: `${i * 90}ms`,
                    }}
                  >
                    <SkeletonBlock w={30} h={30} radius={8} />
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <SkeletonBlock w="35%" h={13} />
                      <SkeletonBlock w="20%" h={10} />
                    </Box>
                    <SkeletonBlock w={60} h={24} radius={8} />
                    <SkeletonBlock w={70} h={24} radius={8} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${alpha("#000", 0.07)}`,
                }}
              >
                <DataTable
                  columns={tab === 0 ? promoColumns : marketerColumns}
                  data={tab === 0 ? promoData : marketers}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 20]}
                  highlightOnHover
                  responsive
                  customStyles={tableCustomStyles}
                  noDataComponent={
                    <Box sx={{ textAlign: "center", py: 5 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 3,
                          background: alpha("#2563EB", 0.07),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 1.5,
                        }}
                      >
                        {tab === 0 ? (
                          <LocalOfferIcon
                            sx={{
                              fontSize: 24,
                              color: "primary.main",
                              opacity: 0.4,
                            }}
                          />
                        ) : (
                          <CampaignIcon
                            sx={{
                              fontSize: 24,
                              color: "#7C3AED",
                              opacity: 0.4,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "text.disabled",
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 600,
                        }}
                      >
                        {tab === 0
                          ? "No promo codes found"
                          : "No marketers found"}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}

export default PromoCodeDashBoard;
